// Invitation d'un partenaire par la fonction `users` (`partner_invite`), depuis le bloc « Partenaires » d'une fiche,
// « Mes partenaires » ou « Partenaires et territoires » ; un compte inconnu est créé invité, dans sa propre organisation.
import { useMutation } from "@tanstack/react-query";
import { useNotify, useTranslate } from "ra-core";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { ACCOUNT_LOCALES, type AccountLocale } from "./accountLocale";
import {
  type InviteOutcome,
  invitePartner,
  partnerSaleIdFor,
} from "./partnerInvite";

export const InvitePartnerDialog = ({
  open,
  onOpenChange,
  onInvited,
  fromProject = false,
  projectId,
  transmitDefault = true,
  dealHolder,
  defaultLocale = "en",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: (
    outcome: InviteOutcome & { partnerSaleId: number; email: string },
    transmitDeal: boolean,
  ) => Promise<boolean>; // vrai si le partage a réussi
  // Depuis une fiche : avec au choix la transmission de l'affaire.
  fromProject?: boolean;
  // Fiche nommée dans l'e-mail d'invitation (avec l'affaire si elle est transmise).
  projectId?: string;
  // Case décochée d'office quand un autre suit l'affaire (même règle que le bloc de la fiche), nommé dans le libellé.
  transmitDefault?: boolean;
  dealHolder?: string;
  // Langue de l'invitation et du compte créé : d'après le pays de la fiche (`inviteLocaleFor`), l'anglais sinon.
  defaultLocale?: AccountLocale;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // null : pas encore touchée, la valeur par défaut suit qui tient l'affaire.
  const [touched, setTransmitDeal] = useState<boolean | null>(null);
  const transmitDeal = touched ?? transmitDefault;
  const [chosenLocale, setLocale] = useState<AccountLocale | null>(null);
  const locale = chosenLocale ?? defaultLocale;
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      invitePartner({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        locale,
        ...(fromProject && projectId
          ? { project_id: projectId, transmit: transmitDeal }
          : {}),
      }),
    onSuccess: async (outcome) => {
      const address = email.trim().toLowerCase();
      if (outcome.kind === "refused") {
        notify(outcome.message, {
          type: "error",
          messageArgs: { email: address },
        });
        return;
      }
      const partnerSaleId = await partnerSaleIdFor(outcome, address);
      if (partnerSaleId == null) {
        notify("resources.projects.partners.invite.failed", { type: "error" });
        return;
      }
      // Échec du partage : son message d'erreur reste seul, le dialogue reste ouvert.
      const shared = await onInvited(
        { ...outcome, partnerSaleId, email: address },
        fromProject && transmitDeal,
      );
      if (!shared) return;
      const args = {
        email: address,
        name: `${firstName.trim()} ${lastName.trim()}`,
      };
      const sharedText = fromProject
        ? " " +
          translate(
            transmitDeal
              ? "resources.projects.partners.shared_deal"
              : "resources.projects.partners.shared",
            args,
          )
        : "";
      notify(translate(outcome.message, args) + sharedText, {
        type:
          outcome.kind === "linked" && outcome.emailFailed ? "warning" : "info",
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      setTransmitDeal(null);
      setLocale(null);
      onOpenChange(false);
    },
    onError: () =>
      notify("resources.projects.partners.invite.failed", { type: "error" }),
  });
  const valid =
    !!firstName.trim() &&
    !!lastName.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid || isPending) return;
    mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.projects.partners.invite.title")}
            </DialogTitle>
            <DialogDescription>
              {translate(
                fromProject
                  ? "resources.projects.partners.invite.lead"
                  : "resources.projects.partners.invite.lead_list",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-first-name">
                {translate("resources.projects.partners.invite.first_name")}
              </Label>
              <Input
                id="invite-first-name"
                autoComplete="off"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-last-name">
                {translate("resources.projects.partners.invite.last_name")}
              </Label>
              <Input
                id="invite-last-name"
                autoComplete="off"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">
              {translate("resources.projects.partners.invite.email")}
            </Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label id="invite-locale">
              {translate("resources.projects.partners.invite.locale")}
            </Label>
            <ToggleGroup
              type="single"
              variant="outline"
              aria-labelledby="invite-locale"
              value={locale}
              onValueChange={(value) =>
                value && setLocale(value as AccountLocale)
              }
              className="justify-start"
            >
              {ACCOUNT_LOCALES.map((code) => (
                <ToggleGroupItem
                  key={code}
                  value={code}
                  lang={code}
                  className="min-h-11 px-4 text-sm"
                >
                  {code === "fr" ? "Français" : "English"}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              {translate(
                fromProject
                  ? "resources.projects.partners.invite.locale_help"
                  : "resources.projects.partners.invite.locale_help_list",
              )}
            </p>
          </div>
          {fromProject && (
            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={transmitDeal}
                onCheckedChange={(checked) => setTransmitDeal(checked === true)}
              />
              <span>
                {dealHolder
                  ? translate("resources.projects.partners.with_deal_held", {
                      name: dealHolder,
                    })
                  : translate("resources.projects.partners.with_deal")}
              </span>
            </label>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button type="submit" disabled={!valid || isPending}>
              {translate(
                fromProject
                  ? "resources.projects.partners.invite.submit"
                  : "resources.projects.partners.invite.submit_list",
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
